import React, { useMemo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Button from "../../components/utils/Button";
import Table from "../../components/utils/Table";
import CardLayout from "../../components/utils/CardLayout";
import { useDispatch, useSelector } from "react-redux";
import reportActions from "../../redux/reports/actions";
import genericActions from "../../redux/generic/actions";
import { downloadExcelSampleFile } from "../../helpers/commonFunction";
import PrintDetail from "../../components/utils/PrintDetail";
import ToastAlert from "../../components/utils/ToastAlert";
import moment from "moment";

const { getACode, printDetail, resetMessage: printResetMessage } = genericActions;
const { getTransactionReports, resetMessage } = reportActions;
const dateFormat = process.env.REACT_APP_DATE_TIME_FORMAT;


const RechargeReport = () => {
    const dispatch = useDispatch();
    const { aCodeList, printDetailList, printDetailLoading, printDetailError } = useSelector(state => state.generic);
    const [searchItem, setSearchItem] = useState("");
    const today = new Date();
    const beforeTwoDays = new Date();
    beforeTwoDays.setDate(beforeTwoDays.getDate() - 2);
    const formatDate = (date) => date.toISOString().split("T")[0];
    const { transactionReports, transactionReportsLoading, } = useSelector((state) => state.reports)
    const { defaultCurrency } = useSelector((state) => state.auth);
    const [selectedTxnId, setSelectedTxnId] = useState("");


    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        mode: "onChange",
        defaultValues: {
            distributorID: "0",
            searchText: "",
            status: "0",
            fromDate: formatDate(beforeTwoDays),
            toDate: formatDate(today),
            isReporttype: 2,
        },
    });

    useEffect(() => {
        dispatch(printResetMessage());
        dispatch(getACode());
        dispatch(getTransactionReports({
            fromDate: formatDate(beforeTwoDays),
            toDate: formatDate(today),
            isReporttype: 2,
            searchText: "",
            status: "0",
        }));
    }, []);

    useEffect(() => {
        if (printDetailError) {
            ToastAlert({ type: "error", text: printDetailError, });
        }
    }, [printDetailError]);


    const statusList = [
        { value: "0", label: "All Status" },
        { value: "23", label: "Pending" },
        { value: "27", label: "Success" },
        { value: "28", label: "Failed" }
    ];

    const columns = [
        { displayName: "Transaction Type", key: "transactionType" },
        { displayName: "Transaction ID", key: "txnid" },
        { displayName: "MSISDN", key: "msisdn" },
        { displayName: "Plan Name", key: "planName" },
        { displayName: "Transaction Date", key: "transactionDate" },
        // { displayName: "Plan Amount", key: "planAmount" },
        { displayName: "Charge Amount", key: "chargeAmount" },
        // { displayName: "Profit", key: "profit" },
        { displayName: "Print", key: "print" },
        { displayName: "Validity Days", key: "validity" },
        { displayName: "Status", key: "status" },
    ];

    const filteredItems = useMemo(() => {
        return Array.isArray(transactionReports) && transactionReports.length > 0
            ? transactionReports.filter(item => {
                if (!searchItem) return true;
                return Object.values(item).some(val => typeof val === "string" && val.toLowerCase().includes(searchItem.toLowerCase()));
            })
            : [];
    }, [transactionReports, searchItem]);

    const handlePrint = (txnid) => {
        const payload = {
            txnid: txnid,
            actionType: 2
        }
        setSelectedTxnId(txnid)
        dispatch(printDetail(payload));
    };

    const rowData = useMemo(() => {
        return (filteredItems || []).map((item) => ({
            transactionType: item["TransactionType"],
            txnid: item["TransactionID"],
            msisdn: item["MobileNumber"],
            planName: item["PlanName"],
            transactionDate: moment(item?.TransactionDate).format(dateFormat),
            // planAmount: defaultCurrency + '' + item["PlanAmount"],
            chargeAmount: defaultCurrency + '' + item["ChargeAmount"],
            // profit: defaultCurrency + '' + item["Profit"],
            print: item?.StatusID == 27 && ((printDetailLoading && selectedTxnId == item?.TransactionID) ?
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                : <button type="button" className="btn btn-sm rounded-circle btn-primary-light bg-primary-subtle" title="Print" onClick={() => handlePrint(item?.TransactionID)} > <i className="ri-printer-line" /> </button>),
            validity: item["ValidityDays"],
            status: (<span className={`badge ${item?.StatusID == 23 ? "bg-warning" : item?.StatusID == 27 ? 'bg-success' : item?.StatusID == 28 ? "bg-danger" : 'bg-info'}`}
            > {item?.Status}
            </span>
            ),
        }));
    }, [filteredItems]);
    const onSubmit = (data) => {
        dispatch(getTransactionReports({
            ...data, distributorID: Number(data.distributorID || 0), isReporttype: 2, status: data.status || "0",
            searchText: data.searchText || "",
        }))
    };

    const handleReset = () => {
        reset({
            distributorID: "0",
            searchText: "",
            fromDate: formatDate(beforeTwoDays),
            toDate: formatDate(today),
            isReporttype: 2,
            status: "0",
        });

        setSearchItem("");
        dispatch(resetMessage());
    };

    const handleExportExcel = () => {
        const data = filteredItems.map(item => ({
            "TransactionType": item["TransactionType"],
            "Transaction ID": item?.TransactionID,
            "MSISDN": item["MobileNumber"],
            "Transaction Date": moment(item?.TransactionDate).format(dateFormat),
            "Plan Name": item?.PlanName,
            // "Plan Amount": defaultCurrency + '' + item["PlanAmount"],
            "Charge Amount": defaultCurrency + '' + item["ChargeAmount"],
            // "Profit": defaultCurrency + '' + item?.Profit,
            "Validity Days": item?.ValidityDays,
            "Status": item["Status"],
        }))

        downloadExcelSampleFile(data, "recharge-report.xlsx");
    };


    return (
        <>
            {!(Array.isArray(printDetailList) && printDetailList.length > 0) ? <CardLayout
                title="Recharge Report"
                icon="fas fa-wallet "
                rightContent={
                    <div className="d-flex flex-wrap align-items-center gap-1">
                        <input
                            type="search"
                            style={{ width: "300px", }}
                            onChange={(e) => setSearchItem(e.target.value)}
                            className="form-control form-control-sm"
                            placeholder="Search..."
                            value={searchItem}
                        />
                        <Button
                            title="Excel"
                            buttonType="button"
                            variant="secondary"
                            onClick={handleExportExcel}
                            icon={<i className="ri-download-cloud-line me-2" />}
                        />
                    </div>} >
                <div>
                    {/* Body */}
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div
                            className="row" >
                            {/* ACode */}
                            <div className="col-lg-2 col-md-4">
                                <label className="form-label fw-semibold">ACode</label>
                                <select className="form-select" {...register("distributorID")} >
                                    <option value="0">All</option>
                                    {aCodeList?.map((item) => (<option key={item.Id} value={item.Id} > {item.ACode} </option>))}
                                </select>
                            </div>

                            {/* Serial Number / MSISDN / TxnID */}
                            <div className="col-lg-3 col-md-6">
                                <label className="form-label fw-semibold">MSISDN / TxnID</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="MSISDN / TxnID"
                                    {...register("searchText")}
                                />
                            </div>
                            {/* From Date */}
                            <div className="col-lg-2 col-md-4">
                                <label className="form-label fw-semibold">From Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    {...register("fromDate")}
                                />
                            </div>

                            {/* To Date */}
                            <div className="col-lg-2 col-md-4">
                                <label className="form-label fw-semibold">To Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    {...register("toDate")}
                                />
                            </div>

                            {/* Status */}
                            <div className="col-lg-2 col-md-4">
                                <label className="form-label fw-semibold">Status</label>
                                <select className="form-select"
                                    {...register("status")}>
                                    {statusList.map((item, index) => (
                                        <option key={index} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-lg-3 col-md-6 d-flex align-items-end gap-3" style={{ height: '50px' }}>
                                {/* Buttons */}
                                <Button
                                    title="Get Report"
                                    buttonType="submit"
                                    variant="primary"
                                    disable={transactionReportsLoading}
                                    loading={transactionReportsLoading}
                                    icon={<i className="ri-search-line me-2"></i>}
                                    style={transactionReportsLoading ? { width: '46.5%', padding: '10px 2px' } : {}}
                                />

                                <Button
                                    title="Reset"
                                    buttonType="button"
                                    variant="secondary"
                                    onClick={handleReset}
                                    icon={<i className="ri-refresh-line me-2"></i>}
                                />
                            </div>

                        </div>
                    </form>

                    {/* Search Form end */}

                </div>
                {/* Table */}
                <div className="mt-2">
                    <Table
                        columns={columns}
                        data={rowData}
                        pagination
                        loading={transactionReportsLoading}
                        tableHeight="285px"
                    />

                </div>
            </CardLayout> : <PrintDetail printDetails={printDetailList[0]} goBack={() => { setSelectedTxnId(''); dispatch(printResetMessage()) }} printType={2} />}
        </>
    );
};

export default RechargeReport;
